<template>
  <div class="fill-height d-flex flex-column">
    <div class="d-flex justify-end mb-6">
      <v-tooltip
        :text="t('raster.tooltip.add_coordinate')"
        bottom
        :open-delay="200"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="outlined"
            data-testid="add-empty-coordinate"
            color="primary"
            style="padding: 0; min-width: 36px"
            width="36px"
            @click="timeseriesStore.addEmptyCoords()"
          >
            <v-icon color="primary">
              mdi-plus
            </v-icon>
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip
        :text="t('raster.tooltip.reset_coordinates')"
        bottom
        :open-delay="200"
        style="z-index: 999999"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="outlined"
            color="error"
            style="padding: 0; min-width: 36px; margin-left: 12px"
            width="36px"
            data-testid="control-panel-reset-all-coordinates"
            @click="timeseriesStore.resetCoords()"
          >
            <v-icon color="error">
              mdi-delete-outline
            </v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </div>
    <div
      class="fill-height"
      style="overflow-y: auto; position: relative; min-height: 78px;"
    >
      <div style="position: absolute; width: 100%;">
        <LatLonChoiceField
          v-for="[id, coords] in Object.entries(timeseriesStore.coords)"
          :id="id"
          :key="id"
          :ref="setRef(id)"
          style="width: 100%"
          :coords="coords"
          :input-help-is-active="inputHelpIsActive"
          class="coordinate"
          :class="{ highlighted: timeseriesStore.highlightedCoordID === id }"
        />
      </div>
    </div>
    <v-checkbox
      v-model="inputHelpIsActive"
      :label="t('raster.input_help_label')"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, nextTick } from 'vue';
import TimeseriesStore from "@/stores/timeseries/timeseries-module";
import LatLonChoiceField from "@/components/raster/LatLonChoiceField.vue";
import { useI18n } from "vue-i18n";

export default defineComponent ({
  name: 'TimeseriesControlPanel',
  components: {
    LatLonChoiceField
  },
  setup() {
    const { t } = useI18n();

    const inputHelpIsActive = ref(true);
    const timeseriesStore = TimeseriesStore;
    const refPrefix = 'coordinate';

    const refsMap = ref<Record<string, InstanceType<typeof LatLonChoiceField> | null>>({});

    const highlightedCoordID = computed<string>(() => {
      return timeseriesStore.highlightedCoordID;
    });

    const setRef = (id: string) => (el: any) => {
      refsMap.value[id] = el;
    };

    watch([highlightedCoordID, inputHelpIsActive], async () => {
      await nextTick();
      const highlightedRef = refsMap.value[highlightedCoordID.value];
      if (highlightedRef?.$el) {
        highlightedRef.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    return {
      t,
      inputHelpIsActive,
      timeseriesStore,
      refPrefix,
      highlightedCoordID,
      setRef,
      refsMap,
    };
  },
});
</script>

<style scoped>
/* when not highlighted no more, transition back */
.coordinate {
  transition: background-color 0.4s;
}
/* filled text-fields have opacity so the background color shines through the textfield. */
:deep(.coordinate.highlighted .v-field__field) {
  background-color: rgb(var(--v-theme-primary-lighten3)) !important;
  transition: 0.4s;
}
</style>
